export type ManualExpenseScope = "installation" | "general_overhead" | "warehouse" | "unallocated";
export type ManualExpenseStatus = "draft" | "planned" | "pending_payment" | "paid" | "cancelled";
export type ManualExpenseSource = "manual" | "import";
export type ManualExpenseAuditAction =
  | "created"
  | "updated"
  | "planned"
  | "pending_payment"
  | "paid"
  | "cancelled"
  | "duplicated"
  | "soft_deleted";

export const MANUAL_EXPENSE_SCOPE_LABELS: Record<ManualExpenseScope, string> = {
  installation: "Instalación / expediente",
  general_overhead: "Gastos generales",
  warehouse: "Almacén",
  unallocated: "Pendiente de imputar",
};

export const MANUAL_EXPENSE_STATUS_LABELS: Record<ManualExpenseStatus, string> = {
  draft: "Borrador",
  planned: "Planificado",
  pending_payment: "Pendiente de pago",
  paid: "Pagado",
  cancelled: "Cancelado",
};

export const EDITABLE_STATUSES: ManualExpenseStatus[] = ["draft", "planned"];
export const TERMINAL_STATUSES: ManualExpenseStatus[] = ["paid", "cancelled"];
export const CANCELLABLE_STATUSES: ManualExpenseStatus[] = ["draft", "planned", "pending_payment"];
export const SOFT_DELETABLE_STATUSES: ManualExpenseStatus[] = ["draft", "cancelled"];

export const VAT_RATES = [0, 4, 10, 21] as const;
export type VatRate = typeof VAT_RATES[number];

export const VAT_RATE_BASIS_POINTS: Record<VatRate, number> = {
  0: 0,
  4: 400,
  10: 1000,
  21: 2100,
};

export const MANUAL_EXPENSE_STATUS_GRAPH: Record<ManualExpenseStatus, ManualExpenseStatus[]> = {
  draft: ["planned", "cancelled"],
  planned: ["pending_payment", "cancelled"],
  pending_payment: ["paid", "cancelled"],
  paid: [],
  cancelled: [],
};

export function isManualExpenseTransitionAllowed(from: ManualExpenseStatus, to: ManualExpenseStatus): boolean {
  return MANUAL_EXPENSE_STATUS_GRAPH[from]?.includes(to) ?? false;
}

export function assertManualExpenseScope(scope: ManualExpenseScope, installationId?: number | null): void {
  if (scope === "installation" && !installationId) {
    throw new Error("INSTALLATION_SCOPE_REQUIRES_INSTALLATION");
  }
  if (scope === "warehouse" && installationId != null) {
    throw new Error("WAREHOUSE_SCOPE_REJECTS_INSTALLATION");
  }
  if (scope === "general_overhead" && installationId != null) {
    throw new Error("GENERAL_OVERHEAD_SCOPE_REJECTS_INSTALLATION");
  }
  if (scope === "unallocated" && installationId != null) {
    throw new Error("UNALLOCATED_SCOPE_REJECTS_INSTALLATION");
  }
}

export function assertManualExpenseDestinationForStatus(scope: ManualExpenseScope, status: ManualExpenseStatus): void {
  if (scope === "unallocated" && status !== "draft" && status !== "cancelled") {
    throw new Error("MANUAL_EXPENSE_DESTINATION_REQUIRED");
  }
}

export type InstallationReference = {
  id: number;
  installationCode?: string | null;
  expedienteCode?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  siteName?: string | null;
  workDescription?: string | null;
  name?: string | null;
  address?: string | null;
  status?: string | null;
};

export function formatInstallationReference(reference?: InstallationReference | null): string {
  if (!reference) return "-";
  const code = (reference.installationCode ?? reference.expedienteCode)?.trim();
  const clientName = reference.clientName?.trim();
  const siteName = reference.siteName?.trim();
  const work = (reference.workDescription ?? reference.name)?.trim();
  const address = reference.address?.trim();
  return [
    code ? `Exp. ${code}` : "Expediente sin código",
    clientName,
    siteName,
    work,
    address,
  ].filter(Boolean).join(" · ");
}

export function installationMatchesSearchQuery(
  inst: {
    id?: number | null;
    installationCode?: string | null;
    expedienteCode?: string | null;
    clientId?: string | null;
    clientName?: string | null;
    siteName?: string | null;
    workDescription?: string | null;
    name?: string | null;
    address?: string | null;
  },
  query: string,
): boolean {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  return [
    inst.installationCode,
    inst.expedienteCode,
    inst.clientId,
    inst.clientName,
    inst.siteName,
    inst.workDescription,
    inst.name,
    inst.address,
  ].some((field) => field != null && String(field).toLowerCase().includes(term));
}

export function isValidIsoDateOnly(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

export function todayMadridDateString(date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function parseEuroInputToCents(value: string): number {
  const normalized = value.trim().replace(",", ".");
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(normalized);
  if (!match) throw new Error("INVALID_MONEY_AMOUNT");
  const euros = BigInt(match[1]);
  const centsPart = (match[2] ?? "").padEnd(2, "0");
  const cents = euros * BigInt(100) + BigInt(centsPart || "0");
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("MONEY_AMOUNT_TOO_LARGE");
  return Number(cents);
}

export function formatCentsAsDecimal(cents: number): string {
  if (!Number.isSafeInteger(cents) || cents < 0) throw new Error("INVALID_CENTS_AMOUNT");
  const whole = Math.floor(cents / 100);
  const fraction = String(cents % 100).padStart(2, "0");
  return `${whole}.${fraction}`;
}

export function computeManualExpenseAmounts(
  taxableBaseCents: number,
  vatRate: VatRate,
): { taxableBaseCents: number; vatAmountCents: number; totalAmountCents: number; taxableBase: string; vatAmount: string; totalAmount: string } {
  if (!Number.isSafeInteger(taxableBaseCents) || taxableBaseCents < 0) throw new Error("INVALID_TAXABLE_BASE_CENTS");
  const basisPoints = VAT_RATE_BASIS_POINTS[vatRate];
  if (basisPoints === undefined) throw new Error("INVALID_VAT_RATE");
  const vatAmountCentsBigInt = (BigInt(taxableBaseCents) * BigInt(basisPoints) + BigInt(5000)) / BigInt(10000);
  if (vatAmountCentsBigInt > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("VAT_AMOUNT_TOO_LARGE");
  const vatAmountCents = Number(vatAmountCentsBigInt);
  const totalAmountCents = taxableBaseCents + vatAmountCents;
  if (!Number.isSafeInteger(totalAmountCents)) throw new Error("TOTAL_AMOUNT_TOO_LARGE");
  return {
    taxableBaseCents,
    vatAmountCents,
    totalAmountCents,
    taxableBase: formatCentsAsDecimal(taxableBaseCents),
    vatAmount: formatCentsAsDecimal(vatAmountCents),
    totalAmount: formatCentsAsDecimal(totalAmountCents),
  };
}

export function computeVatAmounts(taxableBaseCents: number, vatRate: VatRate): { vatAmount: string; totalAmount: string } {
  const amounts = computeManualExpenseAmounts(taxableBaseCents, vatRate);
  return { vatAmount: amounts.vatAmount, totalAmount: amounts.totalAmount };
}

export function validateVatConsistency(taxableBaseCents: number, vatRate: VatRate, providedTotalCents?: number): boolean {
  if (providedTotalCents === undefined) return true;
  return computeManualExpenseAmounts(taxableBaseCents, vatRate).totalAmountCents === providedTotalCents;
}
